import { categories } from 'util/appConstants';

/** Given the raw description transform it into the text to displau */
export default function getComputedDescription(description, category, language, level) {
  let computedDescription = description || '';
  if (computedDescription) {
    computedDescription = getDescriptionWithSwitchOptions(computedDescription, category, language, level);
    computedDescription = getTranslatedDescription(computedDescription, category, language, level);
    computedDescription = getDescriptionWithPreservedTagContent(computedDescription, category, language, level);
    computedDescription = getDescriptionClearedFromTags(computedDescription, category, language, level);
  }
  return computedDescription;
}

/** Escape regex characters in the string so it can be used inside a regex */
const escapeRegExp = (stringToEscape) => stringToEscape.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const getDescriptionWithSwitchOptions = (description, category, language, levelToRun) => {
  // Example: [switch][python]and[/python][default]&&[/default][/switch]
  // Need to use closing tags to handle line breaks and styling since description is html
  let startTag = '[switch]';
  let endTag = '[/switch]';

  let matchedStartTags = description.matchAll(/\[switch\]/g);
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // replace the switch with desired content
      description =
        description.substring(0, startTagIndex) +
        getDescriptionSwitchContent(
          description.substring(startTagIndex + startTag.length, endTagIndex),
          category,
          language,
          levelToRun
        ) +
        description.substring(endTagIndex + endTag.length);
    }
    startTagMatch = matchedStartTags.next();
  }
  return description;
};

const getDescriptionSwitchContent = (switchContent, category, language, levelToRun) => {
  // Get the first match from switchContent or try to find[default][/default]
  let matchedStartTags;
  if (category === categories.EXERCISE.category && levelToRun) {
    // If exercise match on level or language
    let levelTag = 'Level' + levelToRun;
    matchedStartTags = switchContent.matchAll(
      new RegExp('\\[(\\w+\\|)*(' + language + '|' + levelTag + ')(\\|\\w+)*\\]', 'g')
    );
  } else {
    // match only on language
    matchedStartTags = switchContent.matchAll(new RegExp('\\[(\\w+\\|)*' + language + '(\\|\\w+)*\\]', 'g'));
  }
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTag = startTagMatch.value[0];
    let endTag = startTag.replace(/^\[/, '[/');
    let startTagIndex = switchContent.indexOf(startTag);
    let endTagIndex = switchContent.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // tag match found, return it
      return switchContent.substring(startTagIndex + startTag.length, endTagIndex);
    }
    startTagMatch = matchedStartTags.next();
  }

  // No specific tag match found, try to find default
  let startTag = '[default]';
  let endTag = '[/default]';
  let startTagIndex = switchContent.indexOf(startTag);
  let endTagIndex = switchContent.indexOf(endTag, startTagIndex + startTag.length);
  if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
    // default content found, return it
    return switchContent.substring(startTagIndex + startTag.length, endTagIndex);
  }

  return '';
};

const getTranslatedDescription = (description, category, language, level) => {
  // Find all [translate] tags, parse content, use them to update the description
  let startTag = '[translate]';
  let endTag = '[/translate]';
  let matchedStartTags = description.matchAll(/\[translate\]/g);
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // Get parsed content with from and to
      let translationDict = getTranslatedContent(
        description.substring(startTagIndex + startTag.length, endTagIndex),
        category,
        language,
        level
      );
      // Update the description
      description = description.substring(0, startTagIndex) + description.substring(endTagIndex + endTag.length);
      if (translationDict.from) {
        description = description.replaceAll('*' + translationDict.from, translationDict.to);
      }
    }

    startTagMatch = matchedStartTags.next();
  }
  return description;
};

const getTranslatedContent = (translateContent, category, language, level) => {
  let result = {};
  // Find what to translate from
  let startTag = '[from]';
  let endTag = '[/from]';
  let startTagIndex = translateContent.indexOf(startTag);
  let endTagIndex = translateContent.indexOf(endTag, startTagIndex + startTag.length);
  if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
    result.from = translateContent.substring(startTagIndex + startTag.length, endTagIndex);
  }
  // Get what to translate to from switch content function
  result.to = getDescriptionSwitchContent(translateContent, category, language, level);
  return result;
};

const getDescriptionWithPreservedTagContent = (description, category, language, levelToRun) => {
  // Determine what leftover tag values we might want to preserve
  let tagsToPreserve = [language];
  if (category === categories.EXERCISE.category && levelToRun) {
    tagsToPreserve.push('Level' + levelToRun);
  }
  for (let i = 0; i < tagsToPreserve.length; i++) {
    // match all tags like [python] or [python|cpp] etc if you are using python
    let matchedStartTags = description.matchAll(
      new RegExp('\\[(\\w+\\|)*' + escapeRegExp(tagsToPreserve[i]) + '(\\|\\w+)*\\]', 'g')
    );
    let startTagMatch = matchedStartTags.next();
    while (!startTagMatch.done) {
      let startTag = startTagMatch.value[0];
      let endTag = startTag.replace(/^\[/, '[/');
      let startTagIndex = description.indexOf(startTag);
      let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
      if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
        // remove the tags but keep content
        description =
          description.substring(0, startTagIndex) +
          description.substring(startTagIndex + startTag.length, endTagIndex) +
          description.substring(endTagIndex + endTag.length);
      }

      startTagMatch = matchedStartTags.next();
    }
  }
  return description;
};

const getDescriptionClearedFromTags = (description) => {
  // Match all leftover tags and remove them and their content if they are closed
  // opening tag examples: [python], [python|ruby], [Level1|Level2|Level6]
  let matchedStartTags = description.matchAll(/\[(\w+\|)*\w+\]/g);
  let startTagMatch = matchedStartTags.next();
  while (!startTagMatch.done) {
    let startTag = startTagMatch.value[0];
    let endTag = startTag.replace(/^\[/, '[/');
    let startTagIndex = description.indexOf(startTag);
    let endTagIndex = description.indexOf(endTag, startTagIndex + startTag.length);
    if (startTagIndex >= 0 && startTagIndex < endTagIndex) {
      // remove the tags and their content
      description = description.substring(0, startTagIndex) + description.substring(endTagIndex + endTag.length);
    }
    startTagMatch = matchedStartTags.next();
  }
  return description;
};

// const verifyExerciseDescription = (description) => {
//   let errorMessage = '';
//   let possibleTagRegex = /\[\/?Level\d+\]/g;
//   let startTagRegex = /\[Level\d+\]/g;
//   let endTagRegex = /\[\/Level\d+\]/g;

//   let possibleTagRegexAvailable = description.matchAll(possibleTagRegex);
//   let startTagRegexAvailable = description.matchAll(startTagRegex);
//   let endTagRegexAvailable = description.matchAll(endTagRegex);

//   let availableStartTagLevels = [];
//   let availableEndTagLevels = [];
//   let possibleLevels = [];

//   let possibleTag = possibleTagRegexAvailable.next();
//   while (!possibleTag.done) {
//     possibleLevels.push(possibleTag.value[0]);
//     possibleTag = possibleTagRegexAvailable.next();
//   }

//   let endTag = endTagRegexAvailable.next();
//   while (!endTag.done) {
//     availableEndTagLevels.push(endTag.value[0]);
//     endTag = endTagRegexAvailable.next();
//   }

//   let startTag = startTagRegexAvailable.next();
//   while (!startTag.done) {
//     availableStartTagLevels.push(startTag.value[0]);
//     startTag = startTagRegexAvailable.next();
//   }

//   let counter = 0;
//   for (let i = 0; i < possibleLevels.length; i++) {
//     let levelNumber = possibleLevels[i].match(/(\d+)/)[0];
//     let startTag = '[Level' + levelNumber + ']';
//     let endTag = '[/Level' + levelNumber + ']';
//     if (availableStartTagLevels[counter] === startTag && availableEndTagLevels[counter] === endTag) {
//       if (i % 2 !== 0) counter++;
//     } else {
//       errorMessage = startTag + ' tag is not properly handled in editor description.';
//       break;
//     }
//   }

//   if (errorMessage) return $root.popup('implementation-error', { title: 'Error', error: errorMessage });
//   return true;
// }
